import {
  Button,
  ButtonGroup,
  Flex,
  IconButton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { RiArrowRightUpLine } from "@remixicon/react";

import { IcoArchive, IcoFeatureAutoCleanup } from "@/common/components/Icons";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSlugParam } from "@/common/hooks/useParam";
import { CheckListItem } from "@/feature/components/ArchivingComponents";
import { ArchivingModal } from "@/feature/components/ArchivingModal";
import { LinearCleanupIssueLink } from "@/feature/components/LinearCleanupIssueLink";
import { PullRequestLink } from "@/feature/components/PullRequestLink";
import { useFeatureArchivingChecks } from "@/feature/data/useFeatureArchivingChecks";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useArchiveFeature } from "@/feature/data/useFeatureUpdateMutation";
import { useLatestLinearCleanupIssue } from "@/feature/data/useLatestLinearCleanupIssue";
import { useLatestPullRequest } from "@/feature/hooks/useLatestPullRequest";
import { getCleanupPromptLink } from "@reflag/shared/cleanupPrompt";
import pluralize from "@reflag/shared/utils/pluralize";

export function ArchivingSection() {
  const { org } = useEnvironment();
  const modalDisclosure = useDisclosure();
  const { featureId } = useSlugParam("feature");

  const { latestPullRequest } = useLatestPullRequest(featureId!);

  const { data: feature } = useFeatureData(featureId);
  const cleanupIssue = useLatestLinearCleanupIssue(featureId, feature);
  const cleanupPromptLink = feature
    ? getCleanupPromptLink({
        featureName: feature.name,
        featureKey: feature.key,
      })
    : undefined;

  const archive = useArchiveFeature(featureId!);
  const { data: archivingChecks, isLoading: isLoadingArchivingChecks } =
    useFeatureArchivingChecks(featureId);
  const codeRefsMarkedClean = archivingChecks?.codeRefsMarkedClean;

  const shouldShowGuide =
    (!isLoadingArchivingChecks &&
      archivingChecks &&
      archivingChecks.rolledOutStatus &&
      !archivingChecks.isArchived &&
      !archivingChecks.isPermanent) ??
    false;

  const isAutoArchiveOn =
    !!archivingChecks &&
    !archivingChecks.checksPassed &&
    !archivingChecks.isArchived &&
    feature?.autoArchive === true;

  return (
    <Flex direction="column" gap={1}>
      <SectionHeading isLoading={isLoadingArchivingChecks} variant="subtle">
        Archive guide
      </SectionHeading>
      <Flex direction="column" gap={3}>
        {shouldShowGuide && archivingChecks ? (
          <>
            <Flex direction="column" gap={2}>
              <CheckListItem
                _checkIcon={{ size: "sm" }}
                isChecked={archivingChecks.isStale}
                title={`Stale`}
              />
              <CheckListItem
                _checkIcon={{ size: "sm" }}
                isChecked={archivingChecks.codeRefsRemovalStatus}
                rowGap={1}
                title="Remove flag code"
              >
                {(cleanupIssue ||
                  (!codeRefsMarkedClean &&
                    archivingChecks.githubIntegrationEnabled &&
                    latestPullRequest) ||
                  (!archivingChecks.codeRefsRemovalStatus &&
                    archivingChecks.isStale &&
                    cleanupPromptLink)) && (
                  <Flex direction="column" gap={2}>
                    {!codeRefsMarkedClean &&
                      archivingChecks.githubIntegrationEnabled &&
                      latestPullRequest && (
                        <PullRequestLink
                          key={latestPullRequest.id}
                          number={latestPullRequest.number!}
                          status={latestPullRequest.status!}
                          url={latestPullRequest.url!}
                        />
                      )}
                    {cleanupIssue ? (
                      <LinearCleanupIssueLink
                        issue={cleanupIssue.details}
                        url={cleanupIssue.url}
                      />
                    ) : !archivingChecks.codeRefsRemovalStatus &&
                      archivingChecks.isStale &&
                      cleanupPromptLink ? (
                      <Button
                        as="a"
                        href={cleanupPromptLink}
                        leftIcon={<IcoFeatureAutoCleanup size="sm" />}
                        rel="noopener noreferrer"
                        size="xs"
                        target="_blank"
                        variant="outline"
                        width="fit-content"
                      >
                        Open cleanup prompt
                      </Button>
                    ) : null}
                  </Flex>
                )}
              </CheckListItem>
              <CheckListItem
                _checkIcon={{ size: "sm" }}
                isChecked={archivingChecks.lastCheckStatus}
                title={`No check last ${org.archiveAfterLastCheckInDays} ${pluralize("day", org.archiveAfterLastCheckInDays)}`}
              />
            </Flex>
            {isAutoArchiveOn && (
              <Flex align="center" gap={1}>
                <IcoArchive color="dimmed" size="sm" />
                <Text fontSize="sm">Auto-archive is on</Text>
              </Flex>
            )}
            <ButtonGroup>
              {archivingChecks &&
                archivingChecks.checksPassed &&
                !archivingChecks.isArchived && (
                  <Button size="xs" onClick={archive}>
                    Archive now
                  </Button>
                )}
              <Button
                size="xs"
                variant="outline"
                onClick={modalDisclosure.onOpen}
              >
                Show details
              </Button>
            </ButtonGroup>
          </>
        ) : (
          <>
            <Flex align="center" direction="row" justify="space-between">
              {archivingChecks?.isArchived ? (
                <Text fontSize="sm">The flag is archived</Text>
              ) : archivingChecks?.isPermanent ? (
                <Text fontSize="sm">The flag is permanent</Text>
              ) : (
                <Text fontSize="sm">Use when rolled out to everyone</Text>
              )}
              <IconButton
                aria-label="Show details"
                color="dimmed"
                icon={<RiArrowRightUpLine size={16} />}
                mr={-2}
                size="xs"
                variant="ghost"
                onClick={modalDisclosure.onOpen}
              />
            </Flex>
          </>
        )}
      </Flex>
      <ArchivingModal
        featureId={featureId!}
        modalDisclosure={modalDisclosure}
      />
    </Flex>
  );
}
